// DISDownrange Ballistic Solver v2 - calibrated exponential decay
// src/lib/ballistics.js
// Fast ballistic solver for DISDownrange
// Uses simplified analytical approach - computes in under 1ms

export const MV_CORRECTIONS = {
  '5.56 NATO':   { 10:-300, 12:-225, 14.5:-175, 16:-125, 18:-50, 20:0, 22:40, 24:75 },
  '.223 Rem':    { 16:-100, 18:-50, 20:0, 22:40, 24:75, 26:100 },
  '.308 Win':    { 16:-150, 18:-75, 20:0, 22:50, 24:90, 26:120 },
  '7.62x51 NATO':{ 16:-150, 18:-75, 20:0, 22:50, 24:90 },
  '6.5 Creedmoor':{ 18:-100, 20:-40, 22:0, 24:50, 26:90 },
  '6.5 PRC':     { 20:-80, 22:-30, 24:0, 26:50 },
  '7mm Rem Mag': { 20:-60, 22:0, 24:50, 26:90 },
  '.300 Win Mag':{ 22:-80, 24:0, 26:60, 28:110 },
  '.30-06 Springfield':{ 20:-60, 22:0, 24:50, 26:90 },
  '.243 Win':    { 20:-50, 22:0, 24:50, 26:80 },
  '.270 Win':    { 20:-60, 22:0, 24:50, 26:80 },
  '.338 Lapua Mag':{ 24:0, 26:50, 28:90 },
  '.350 Legend': { 16:-80, 18:-30, 20:0 },
  '.450 Bushmaster':{ 16:-60, 18:-20, 20:0 },
}

export function getMVCorrection(caliber, barrelLength) {
  const table = MV_CORRECTIONS[caliber]
  if (!table) return 0
  const lengths = Object.keys(table).map(Number).sort((a,b) => a-b)
  const nearest = lengths.reduce((prev, curr) =>
    Math.abs(curr - barrelLength) < Math.abs(prev - barrelLength) ? curr : prev
  )
  return table[nearest]
}

export function correctedMV(baseMV, caliber, barrelLength) {
  return baseMV + getMVCorrection(caliber, barrelLength)
}

export function calculateDropTable(params) {
  const {
    mv, bc, bulletWeightGr, sightHeightIn = 1.5,
    zeroYards = 100, maxYards = 500, stepYards = 25,
    tempF = 59, altitudeFt = 0, windMph = 0, windAngleDeg = 90
  } = params

  if (!mv || !bc || mv <= 0 || bc <= 0) return []

  const da = altitudeFt + (tempF - 59) * 120
  const bcCorr = Math.max(bc * (1 + da / 50000), 0.001)
  const F = 166 / (bcCorr * 144)
  const crossWind = windMph * Math.sin(windAngleDeg * Math.PI / 180)

  function velAt(yards) {
    // Exponential decay model calibrated to real G1 ballistic data
    // k scales inversely with BC — higher BC = less drag = slower decay
    const k = 0.000764 * (0.270 / bcCorr)
    return Math.max(mv * Math.exp(-k * yards), 50)
  }

  function tof(yards) {
    const ft = yards * 3
    const v1 = velAt(yards)
    // Use average velocity for time of flight
    return ft / ((mv + v1) / 2)
  }

  let angle = 0
  for (let i = 0; i < 10; i++) {
    const zft = zeroYards * 3
    const zt = tof(zeroYards)
    const zdrop = (-0.5 * 32.174 * zt * zt + Math.tan(angle) * zft) * 12 - sightHeightIn
    angle -= zdrop / (zft * 12) * 0.3
  }

  const rows = []
  for (let yards = stepYards; yards <= maxYards; yards += stepYards) {
    const ft = yards * 3
    const vel = Math.max(velAt(yards), 0)
    const t = tof(yards)
    const dropIn = (-0.5 * 32.174 * t * t + Math.tan(angle) * ft) * 12 - sightHeightIn
    const energy = Math.round((bulletWeightGr * vel * vel) / 450400)
    const windDrift = crossWind * (t - ft / mv) * 12

    rows.push({
      distance: yards,
      drop: parseFloat(dropIn.toFixed(1)),
      velocity: Math.round(vel),
      energy: Math.max(energy, 0),
      windDrift: parseFloat(windDrift.toFixed(1)),
      time: parseFloat(t.toFixed(3)),
    })
  }
  return rows
}

export function calcMPBR(params, vitalZoneIn = 10) {
  const table = calculateDropTable({ ...params, maxYards: 700, stepYards: 5 })
  const half = vitalZoneIn / 2
  let mpbr = 0
  for (const row of table) {
    if (Math.abs(row.drop) <= half) mpbr = row.distance
    else if (row.drop < -half) break
  }
  return mpbr
}

export function calcEthicalRange(params, minExpansionVelocity) {
  const table = calculateDropTable({ ...params, maxYards: 700, stepYards: 5 })
  let range = 0
  for (const row of table) {
    if (row.velocity >= minExpansionVelocity) range = row.distance
    else break
  }
  return range
}

export function twistCompatibility(twistRate, bulletWeight, caliber) {
  if (!caliber.includes('5.56') && !caliber.includes('.223')) return null
  const twist = parseFloat(twistRate.replace('1:', ''))
  const gr = parseInt(bulletWeight)
  if (twist <= 7 && gr < 62) return { level: 'warning', msg: `${gr}gr may be overstabilized in ${twistRate}` }
  if (twist >= 9 && gr > 69) return { level: 'warning', msg: `${gr}gr may be understabilized in ${twistRate}` }
  if (twist >= 12 && gr > 55) return { level: 'error', msg: `${gr}gr NOT recommended for ${twistRate}` }
  return null
}
